import type { CSSProperties } from 'react';
import { seatsOfTeam, type PlayerStats, type Seat, type TeamId, type Totals } from '../game/match';
import { announce, guaaTallyPhrases, voiceSupported } from '../lib/voice';
import type { SeatPlayer, Teams } from '../shared/schemas';
import { Avatar, Name, teamAvatarFill } from './ui';

interface Props {
  result: TeamId | 'tie';
  teams: Teams;
  seats: SeatPlayer[];
  players: Record<Seat, PlayerStats>;
  guaa: { A: number; B: number };
  subtitle: string;
  /** When the match was finished (ISO); shown on the seal. */
  finishedAt?: string | null;
}

/** A tilted rubber-stamp seal with the finish date and time, sat in its own footer row. */
function DateSeal({ at }: { at: string }) {
  const d = new Date(at);
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const ink = 'var(--color-gold-soft)';
  return (
    <div className="animate-pop mt-5 flex justify-end" style={{ animationDelay: '0.9s' }} role="img" aria-label={`Finished ${date}, ${time}`}>
      <div className="-rotate-6 opacity-90" style={{ width: 72, height: 72 }}>
        <svg viewBox="0 0 100 100" className="h-full w-full drop-shadow-[0_2px_3px_rgb(0_0_0/0.45)]">
          <defs>
            <path id="seal-arc" d="M50 50 m-36 0 a36 36 0 1 1 72 0 a36 36 0 1 1 -72 0" />
          </defs>
          <circle cx="50" cy="50" r="47" fill="rgb(7 42 24 / 0.55)" stroke={ink} strokeWidth="2.5" />
          <circle cx="50" cy="50" r="42" fill="none" stroke={ink} strokeWidth="0.8" strokeDasharray="2 2.2" />
          <circle cx="50" cy="50" r="27" fill="none" stroke={ink} strokeWidth="1.2" />
          <text fill={ink} fontSize="8.2" fontWeight="800" letterSpacing="2.2">
            <textPath href="#seal-arc" startOffset="0">
              RANG ✦ CHAMPIONS ✦ RANG ✦
            </textPath>
          </text>
          <text x="50" y="48" textAnchor="middle" fill={ink} fontSize="13" fontWeight="800" fontFamily="var(--font-display)">
            {date}
          </text>
          <line x1="34" y1="53" x2="66" y2="53" stroke={ink} strokeWidth="0.8" />
          <text x="50" y="63" textAnchor="middle" fill={ink} fontSize="8.5" fontWeight="700">
            {time}
          </text>
        </svg>
      </div>
    </div>
  );
}

/** Twinkling suits and sparkles scattered over the hero. */
const SPARKLES: { ch: string; style: CSSProperties }[] = [
  { ch: '✦', style: { top: '10%', left: '8%', animationDelay: '0s' } },
  { ch: '♠', style: { top: '18%', right: '10%', animationDelay: '0.6s' } },
  { ch: '✧', style: { top: '46%', left: '5%', animationDelay: '1.2s' } },
  { ch: '♥', style: { top: '56%', right: '6%', animationDelay: '0.3s' } },
  { ch: '✦', style: { bottom: '14%', left: '14%', animationDelay: '1.6s' } },
  { ch: '♦', style: { bottom: '20%', right: '16%', animationDelay: '0.9s' } },
  { ch: '♣', style: { top: '6%', left: '46%', animationDelay: '2s' } },
];

function GuaaChip({ n, light = false }: { n: number; light?: boolean }) {
  if (n === 0) {
    return (
      <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${light ? 'bg-white/15 text-gold-soft' : 'bg-success-700/10 text-success-700'}`}>
        0 Guaa ✨
      </span>
    );
  }
  return <span className="rounded-full bg-suit-red px-2.5 py-0.5 text-xs font-bold text-white tabular-nums">{n} Guaa</span>;
}

/** The celebration: champions with crowns (or both teams on a tie). */
export function ResultHero({ result, teams, seats, players, guaa, subtitle, finishedAt }: Props) {
  const tie = result === 'tie';
  const winner = tie ? null : teams[result];
  const glow = winner?.color ?? '#d4a84b';

  return (
    <section
      className="relative overflow-hidden rounded-3xl px-5 pt-8 pb-7 text-center text-cream shadow-[0_20px_50px_-20px_rgb(0_0_0/0.8)] ring-1 ring-gold/40"
      style={{
        background: `radial-gradient(120% 80% at 50% 0%, ${glow}66 0%, transparent 60%), radial-gradient(90% 60% at 50% 100%, rgb(212 168 75 / 0.25), transparent 70%), linear-gradient(180deg, var(--color-felt-800), var(--color-felt-950))`,
      }}
      aria-labelledby="result-h"
    >
      {/* Light rays behind the trophy */}
      <div
        aria-hidden
        className="animate-spin-slow pointer-events-none absolute top-[-60%] left-1/2 h-[220%] w-[220%] -translate-x-1/2 opacity-25"
        style={{
          background:
            'repeating-conic-gradient(from 0deg, rgb(236 212 143 / 0.55) 0deg 6deg, transparent 6deg 22deg)',
          maskImage: 'radial-gradient(circle at center, black 0%, transparent 45%)',
          WebkitMaskImage: 'radial-gradient(circle at center, black 0%, transparent 45%)',
        }}
      />
      {SPARKLES.map((s, i) => (
        <span key={i} aria-hidden className="animate-twinkle pointer-events-none absolute text-lg text-gold-soft" style={s.style}>
          {s.ch}
        </span>
      ))}

      <div className="relative">
        <span
          className="inline-block bg-gold px-6 py-1 text-[11px] font-extrabold tracking-[0.3em] text-ink shadow-md"
          style={{ clipPath: 'polygon(0 0, 100% 0, 94% 50%, 100% 100%, 0 100%, 6% 50%)' }}
        >
          {tie ? 'ALL SQUARE' : 'CHAMPIONS'}
        </span>
        <p className="animate-pop mt-3 text-6xl drop-shadow-[0_6px_10px_rgb(0_0_0/0.5)]" aria-hidden>
          {tie ? '🤝' : '🏆'}
        </p>
        <h1
          id="result-h"
          className="mt-2 font-display text-4xl leading-tight font-extrabold"
          style={{ textShadow: '0 2px 0 rgb(0 0 0 / 0.35), 0 0 24px rgb(236 212 143 / 0.45)' }}
        >
          {tie ? "It's a tie!" : `${winner!.name} win!`}
        </h1>
        <p className="mt-1 text-sm text-cream/75">{subtitle}</p>

        {tie ? (
          <div className="mt-6 grid grid-cols-2 gap-3">
            {(['A', 'B'] as const).map((id) => (
              <div key={id} className="rounded-2xl bg-black/20 p-3 ring-1 ring-white/10">
                <p className="mb-2 truncate text-sm font-bold" style={{ color: teams[id].color }}>
                  {teams[id].name} · {guaa[id]} Guaa
                </p>
                <div className="flex justify-center gap-3">
                  {seatsOfTeam(id).map((s) => (
                    <div key={s} className="flex min-w-0 flex-col items-center">
                      <Avatar name={seats[s]!.name} avatarKey={seats[s]!.avatarKey} size={52} ring={teams[id].color} fill={teamAvatarFill(teams[id].color)} />
                      <Name className="mt-1 max-w-20 truncate text-xs font-semibold">{seats[s]!.name}</Name>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-11 flex justify-center gap-8">
            {seatsOfTeam(result).map((s, i) => (
              <div key={s} className="animate-pop flex min-w-0 flex-col items-center" style={{ animationDelay: `${0.2 + i * 0.15}s` }}>
                <div className="relative">
                  <span aria-hidden className="animate-bob absolute -top-7 left-1/2 -ml-4 text-3xl drop-shadow-md">
                    👑
                  </span>
                  <Avatar name={seats[s]!.name} avatarKey={seats[s]!.avatarKey} size={96} ring="var(--color-gold)" fill={teamAvatarFill(winner!.color)} />
                </div>
                <Name className="mt-2 max-w-28 truncate text-lg font-bold">{seats[s]!.name}</Name>
                <span className="mt-1">
                  <GuaaChip n={players[s].guaa} light />
                </span>
              </div>
            ))}
          </div>
        )}
        {finishedAt && <DateSeal at={finishedAt} />}
      </div>
    </section>
  );
}

/** The losing team's photos, each with a sobbing emoji and their Guaa. */
function LoserTile({ seat, team, player, stats }: { seat: Seat; team: Teams[TeamId]; player: SeatPlayer; stats: PlayerStats }) {
  const canSpeak = voiceSupported();
  return (
    <li key={seat} className="flex flex-col items-center rounded-2xl bg-white/[0.035] p-3 text-center ring-1 ring-white/5">
      <div className="relative">
        <Avatar name={player.name} avatarKey={player.avatarKey} size={72} ring={team.color} fill={teamAvatarFill(team.color)} className="grayscale-[35%]" />
        <span aria-hidden className="animate-sob absolute -right-2 -bottom-1 text-2xl drop-shadow">
          😭
        </span>
      </div>
      <Name className="mt-2 max-w-full truncate font-bold text-cream">{player.name}</Name>
      <span className="mt-1">
        <GuaaChip n={stats.guaa} light />
      </span>
      <p className="mt-1 text-xs text-cream/60">{stats.called ? `Chose Rang ${stats.called}× · saved ${stats.saved}` : "Didn't choose Rang"}</p>
      {canSpeak && (
        <button
          type="button"
          onClick={() => announce(guaaTallyPhrases(player.name, stats.guaa), { force: true })}
          className="mt-2 rounded-full px-3 py-1 text-xs font-semibold text-cream/80 ring-1 ring-white/15 hover:bg-white/10"
          aria-label={`Say ${player.name}'s Guaa in Hindi`}
        >
          🔊 Tease
        </button>
      )}
    </li>
  );
}

/**
 * One merged "result ledger" card: the losing team (photos, crying emoji, their
 * Guaa) on top, and the final score as a seamless footer strip underneath —
 * replacing what used to be two separate cards.
 */
export function LosersCard({
  loser,
  teams,
  seats,
  players,
  guaa,
  totals: t,
  onOpenDetails,
}: {
  loser: TeamId;
  teams: Teams;
  seats: SeatPlayer[];
  players: Record<Seat, PlayerStats>;
  guaa: number;
  totals: Totals;
  onOpenDetails: () => void;
}) {
  const team = teams[loser];
  return (
    <section
      className="animate-rise overflow-hidden rounded-3xl text-cream shadow-[0_16px_40px_-18px_rgb(0_0_0/0.75)] ring-1 ring-white/10"
      style={{ background: `linear-gradient(165deg, var(--color-felt-800) 0%, var(--color-felt-900) 55%, var(--color-felt-950) 100%)` }}
      aria-labelledby="losers-h"
    >
      <div className="relative overflow-hidden px-4 pt-4 pb-3">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{ background: `radial-gradient(70% 90% at 0% 0%, ${team.color}33, transparent 65%)` }}
        />
        <div className="relative flex items-center gap-3">
          <span className="animate-sob text-2xl" aria-hidden>
            😭
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="losers-h" className="font-display text-xl leading-tight font-bold">
              Better luck next time
            </h2>
            <p className="truncate text-sm text-cream/70">
              <span className="font-semibold" style={{ color: team.color }}>
                {team.name}
              </span>{' '}
              · {guaa} Guaa in total
            </p>
          </div>
        </div>
      </div>

      <ul className="grid grid-cols-2 gap-3 px-4 pb-4">
        {seatsOfTeam(loser).map((s) => (
          <LoserTile key={s} seat={s} team={team} player={seats[s]!} stats={players[s]} />
        ))}
      </ul>

      {/* Final score, folded into the same card as a dark ledger footer. */}
      <button
        type="button"
        onClick={onOpenDetails}
        aria-haspopup="dialog"
        className="block w-full border-t border-white/10 bg-black/25 px-5 py-4 text-left transition hover:bg-black/35"
      >
        <p className="mb-3 text-center text-[10px] font-bold tracking-[0.25em] text-gold-soft/80 uppercase">Final score</p>
        <div className="flex items-center gap-3">
          {(['A', 'B'] as const).map((id) => (
            <div key={id} className={`flex min-w-0 flex-1 flex-col ${id === 'A' ? 'items-start text-left' : 'items-end text-right'}`}>
              <span className="flex max-w-full items-center gap-1.5">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: teams[id].color }} aria-hidden />
                <span className="truncate text-sm font-bold">{teams[id].name}</span>
              </span>
              <span
                className="font-display text-4xl leading-none font-extrabold tabular-nums"
                style={{ color: teams[id].color, filter: 'brightness(1.25)' }}
              >
                {id === 'A' ? t.guaaA : t.guaaB}
              </span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-center text-xs font-bold text-gold-soft">Who got Guaa? ›</p>
      </button>
    </section>
  );
}
